import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories, categoryImages, faqItems, howSteps } from "@/mocks/categories";
import { articleList } from "@/mocks/articles";

const SITE_URL = typeof __VITE_SITE_URL__ !== "undefined" ? __VITE_SITE_URL__ : "https://kakobuypolska.top";

const articleImages: Record<number, string> = {
  1: "https://readdy.ai/api/search-image?query=Clean%20modern%20desk%20setup%20with%20laptop%20screen%20showing%20product%20spreadsheet%20data%20surrounded%20by%20fashion%20sneakers%20hoodie%20and%20accessories%20warm%20cream%20terracotta%20background%20soft%20natural%20window%20light%20editorial%20photography%20lifestyle%20workspace%20aesthetic%20high%20resolution%20nordic%20style&width=500&height=380&seq=home-art-01&orientation=landscape",
  2: "https://readdy.ai/api/search-image?query=Beginner%20friendly%20desk%20setup%20with%20open%20laptop%20showing%20shopping%20website%20next%20to%20coffee%20cup%20and%20notebook%20on%20wooden%20table%20warm%20morning%20light%20cozy%20home%20office%20atmosphere%20clean%20minimalist%20aesthetic%20soft%20cream%20and%20sage%20tones%20editorial%20photography%20lifestyle%20shot&width=500&height=380&seq=home-art-02&orientation=landscape",
  3: "https://readdy.ai/api/search-image?query=Collection%20of%20stylish%20sneakers%20arranged%20on%20warm%20terracotta%20surface%20with%20natural%20light%20different%20styles%20and%20colors%20casual%20and%20sporty%20footwear%20editorial%20product%20photography%20clean%20composition%20soft%20shadows%20lifestyle%20aesthetic%20high%20resolution&width=500&height=380&seq=home-art-03&orientation=landscape",
  4: "https://readdy.ai/api/search-image?query=Assortment%20of%20cozy%20hoodies%20and%20sweaters%20in%20neutral%20earth%20tones%20neatly%20folded%20on%20clean%20minimalist%20surface%20warm%20lighting%20soft%20textures%20fabric%20details%20visible%20editorial%20fashion%20photography%20lifestyle%20aesthetic%20cream%20background%20subtle%20shadows&width=500&height=380&seq=home-art-04&orientation=landscape",
  5: "https://readdy.ai/api/search-image?query=Professional%20workspace%20with%20shield%20icon%20on%20laptop%20screen%20representing%20online%20shopping%20security%20next%20to%20smartphone%20and%20stylish%20products%20warm%20natural%20light%20modern%20office%20setting%20soft%20cream%20background%20clean%20aesthetic%20trust%20safety%20concept%20editorial%20photography&width=500&height=380&seq=home-art-05&orientation=landscape",
  6: "https://readdy.ai/api/search-image?query=Cardboard%20package%20box%20next%20to%20world%20map%20with%20Poland%20highlighted%20on%20warm%20wooden%20desk%20natural%20light%20logistics%20shipping%20concept%20with%20fashion%20accessories%20nearby%20clean%20aesthetic%20editorial%20composition%20soft%20cream%20background&width=500&height=380&seq=home-art-06&orientation=landscape",
  7: "https://readdy.ai/api/search-image?query=Elegant%20flat%20lay%20of%20fashion%20accessories%20tote%20bags%20leather%20belts%20sunglasses%20and%20watches%20arranged%20artistically%20on%20warm%20neutral%20background%20soft%20natural%20light%20minimal%20composition%20editorial%20style%20luxury%20feel%20high%20detail%20refined%20aesthetic&width=500&height=380&seq=home-art-07&orientation=landscape",
  8: "https://readdy.ai/api/search-image?query=Comparison%20concept%20with%20laptop%20and%20tablet%20side%20by%20side%20showing%20different%20shopping%20interfaces%20modern%20clean%20desk%20setup%20warm%20lighting%20professional%20workspace%20aesthetic%20soft%20neutral%20tones%20editorial%20photography%20organized%20composition&width=500&height=380&seq=home-art-08&orientation=landscape",
  9: "https://readdy.ai/api/search-image?query=Trendy%20fashion%20moodboard%20with%20sneakers%20streetwear%20pieces%20and%20accessories%20on%20creative%20workspace%20with%20color%20swatches%20warm%20modern%20aesthetic%20editorial%20style%20lifestyle%20photography%20soft%20natural%20light%20inspirational%20composition%20clean%20background&width=500&height=380&seq=home-art-09&orientation=landscape",
};

export default function Home() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string) => {
    window.open(url, "_blank", "noopener,nofollow");
    setDropdownOpen(false);
  };

  const handleArticleClick = () => {
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Kakobuy Spreadsheet Polska",
            url: `${SITE_URL}/`,
            description: "Kakobuy Spreadsheet Polska – największa baza produktów Kakobuy w języku polskim. Znajdź najlepsze linki, ceny, kategorie i poradniki zakupowe.",
            inLanguage: "pl-PL",
            potentialAction: {
              "@type": "SearchAction",
              target: `${SITE_URL}/?q={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Kakobuy Spreadsheet Polska",
            url: `${SITE_URL}/`,
            logo: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png",
            description: "Największa polska baza produktów Kakobuy – uporządkowane kategorie, sprawdzone linki i poradniki zakupowe.",
            sameAs: ["https://kaspreadsheet.com"],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((item) => ({
              "@type": "Question",
              name: t(item.questionKey),
              acceptedAnswer: {
                "@type": "Answer",
                text: t(item.answerKey),
              },
            })),
          }),
        }}
      />

      {/* ===== HERO ===== */}
      <header className="relative w-full min-h-[100vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20fashion%20collage%20with%20sneakers%20hoodies%20bags%20and%20accessories%20floating%20composition%20on%20warm%20gradient%20background%20from%20cream%20to%20terracotta%20soft%20dreamy%20lighting%20editorial%20magazine%20style%20creative%20layout%20gentle%20bokeh%20effect%20ethereal%20atmosphere%20unique%20visual%20identity&width=1600&height=900&seq=kakobuy-hero-v2-01&orientation=landscape"
            alt="Kakobuy Spreadsheet - produkty i akcesoria"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        </div>

        <div className="relative z-10 w-full px-6 md:px-10 py-32 md:py-40">
          <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16 max-w-[1400px] mx-auto">
            <div className="lg:w-[60%]">
              <span className="inline-block px-4 py-1.5 bg-background-50/90 backdrop-blur-sm text-xs font-medium text-foreground-800 rounded-full mb-6">
                <i className="ri-sparkling-line mr-1.5 text-primary-500"></i>
                {t("hero_tag")}
              </span>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-background-50 leading-[1.08] tracking-tight">
                {t("hero_title_line1")}
                <br />
                {t("hero_title_line2")}
                <br />
                {t("hero_title_line3")}
              </h1>
            </div>

            <div className="lg:w-[40%] flex flex-col gap-6 lg:pt-4">
              <p className="text-base md:text-lg text-background-50/85 leading-relaxed">
                {t("hero_subtitle")}
              </p>

              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full flex items-center justify-between px-5 py-3.5 bg-background-50/15 backdrop-blur-md border border-background-50/20 rounded-2xl text-sm text-background-50 hover:bg-background-50/25 transition-all cursor-pointer"
                >
                  <span>{t("dropdown_placeholder")}</span>
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 right-0 bg-background-50 rounded-2xl overflow-hidden z-50 max-h-[320px] overflow-y-auto">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategorySelect(cat.url)}
                        className="w-full text-left px-5 py-3 text-sm text-foreground-800 hover:bg-accent-100/60 transition-colors cursor-pointer border-b border-background-200/50 last:border-none whitespace-nowrap"
                      >
                        {t(cat.nameKey)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 bg-background-50 text-foreground-950 text-sm font-semibold rounded-xl hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t("hero_cta_primary")}
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 text-background-50 text-sm font-medium underline underline-offset-4 hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t("hero_cta_secondary")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ===== PRODUCT CATEGORY GRID ===== */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-background-50">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-14">
            <span className="inline-block text-xs font-medium text-accent-600 mb-3">
              <i className="ri-grid-line mr-1.5"></i>Kategorie Produktów
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight max-w-2xl">
              Przeglądaj Kategorie Kakobuy Spreadsheet
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group relative aspect-[3/4] rounded-2xl overflow-hidden cursor-pointer"
              >
                <img
                  src={categoryImages[cat.imageSeq]}
                  alt={t(cat.nameKey)}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                  <h3 className="text-base md:text-lg font-heading font-semibold text-background-50">
                    {t(cat.nameKey)}
                  </h3>
                  <p className="text-xs text-background-50/70 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {t("hero_cta_primary")} →
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHAT IS KAKOBUY SPREADSHEET ===== */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-background-100">
        <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row gap-12 lg:gap-20 items-center">
          <div className="lg:w-[50%]">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20bright%20workspace%20with%20laptop%20displaying%20spreadsheet%20data%20surrounded%20by%20fashion%20items%20sneakers%20on%20wooden%20desk%20sunlit%20room%20with%20plants%20warm%20natural%20lighting%20creative%20studio%20aesthetic%20editorial%20photography%20cozy%20atmosphere%20clean%20composition&width=700&height=500&seq=kakobuy-what-is-v2-01&orientation=landscape"
              alt="Kakobuy Spreadsheet - co to jest"
              className="w-full rounded-3xl object-cover"
            />
          </div>
          <div className="lg:w-[50%]">
            <span className="inline-block px-3 py-1 bg-accent-100/80 text-accent-700 text-xs font-medium rounded-xl mb-4">
              <i className="ri-information-line mr-1"></i>Kakobuy Spreadsheet
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-6">
              {t("section_what_is_title")}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
              {t("section_what_is_desc")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-background-50">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-accent-500 text-base"></i>
                  </div>
                  <span className="text-sm text-foreground-700">{t(`section_what_is_point${i}`)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== WHY USE ===== */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-background-50">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950">
              {t("section_why_title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Card 1: Save Time */}
            <div className="rounded-2xl overflow-hidden bg-background-100 hover:bg-background-200/60 transition-colors">
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Minimalist%20desk%20with%20hourglass%20next%20to%20laptop%20showing%20organized%20spreadsheet%20warm%20golden%20hour%20light%20through%20window%20productive%20calm%20atmosphere%20clean%20aesthetic%20cozy%20workspace%20with%20fashion%20items%20in%20background%20soft%20tones%20editorial%20photography&width=500&height=280&seq=why-card-01&orientation=landscape"
                  alt="Oszczędzaj czas - Kakobuy Spreadsheet"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 md:p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mb-4">
                  <i className="ri-time-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-3">
                  {t("section_why_card1_title")}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t("section_why_card1_desc")}
                </p>
              </div>
            </div>

            {/* Card 2: Better Products */}
            <div className="rounded-2xl overflow-hidden bg-background-100 hover:bg-background-200/60 transition-colors">
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Curated%20product%20display%20with%20sneakers%20hoodie%20and%20accessories%20on%20warm%20wooden%20surface%20golden%20evening%20light%20streaming%20through%20window%20lifestyle%20aesthetic%20premium%20quality%20items%20editorial%20photography%20soft%20shadows%20rich%20textures%20inviting%20atmosphere&width=500&height=280&seq=why-card-02&orientation=landscape"
                  alt="Lepsze produkty - Kakobuy Spreadsheet"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 md:p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mb-4">
                  <i className="ri-star-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-3">
                  {t("section_why_card2_title")}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t("section_why_card2_desc")}
                </p>
              </div>
            </div>

            {/* Card 3: Easier Shopping */}
            <div className="rounded-2xl overflow-hidden bg-background-100 hover:bg-background-200/60 transition-colors">
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Happy%20person%20browsing%20online%20shopping%20on%20laptop%20with%20coffee%20nearby%20in%20bright%20modern%20cafe%20setting%20warm%20natural%20light%20relaxed%20atmosphere%20lifestyle%20photography%20clean%20aesthetic%20genuine%20expression%20casual%20style%20soft%20tones%20editorial%20quality&width=500&height=280&seq=why-card-03&orientation=landscape"
                  alt="Łatwiejsze zakupy - Kakobuy Spreadsheet"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 md:p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mb-4">
                  <i className="ri-shopping-bag-3-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-3">
                  {t("section_why_card3_title")}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t("section_why_card3_desc")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-background-100">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950">
              {t("section_how_title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {howSteps.map((step) => (
              <div key={step.number} className="text-center p-6">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-5">
                  <span className="text-xl font-heading font-bold text-primary-600">{step.number}</span>
                </div>
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-950 mb-2">
                  {t(step.titleKey)}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t(step.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 9 SEO ARTICLE CARDS ===== */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-background-50">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-14">
            <span className="inline-block text-xs font-medium text-foreground-500 mb-2 font-[var(--font-heading)] italic">
              {t("section_article_subtitle")}{" "}
              <span className="font-[var(--font-heading)] italic font-semibold text-foreground-700">{t("section_article_subtitle_italic")}</span>
            </span>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-950 leading-tight max-w-3xl">
              {t("section_article_title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {articleList.map((article, idx) => {
              const num = idx + 1;
              return (
                <Link
                  key={article.slug}
                  to={`/artykul/${article.slug}`}
                  onClick={handleArticleClick}
                  className="group rounded-[28px] overflow-hidden bg-background-100 hover:bg-background-200/60 transition-colors cursor-pointer"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={articleImages[num] || article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 leading-snug mb-2 line-clamp-2">
                      {t(`article_card_${num}_title`) || article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                      {t(`article_card_${num}_excerpt`) || article.excerpt}
                    </p>
                    <span className="text-xs font-medium text-primary-600 group-hover:text-primary-500 transition-colors">
                      {t("section_article_read_more")}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== ABOUT / TRUST SECTION ===== */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-background-100">
        <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
          <div className="lg:w-[50%]">
            <div className="rounded-[36px] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Curated%20product%20collage%20with%20sneakers%20hoodie%20bags%20and%20accessories%20artistically%20arranged%20on%20warm%20neutral%20background%20editorial%20fashion%20aesthetic%20soft%20natural%20lighting%20gentle%20shadows%20modern%20composition%20high%20resolution%20professional%20product%20photography%20with%20varied%20textures&width=680&height=800&seq=kakobuy-about-v2-01&orientation=portrait"
                alt="Kakobuy Spreadsheet - produkty"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:w-[50%]">
            <span className="inline-block px-3 py-1 bg-accent-100/80 text-accent-700 text-xs font-medium rounded-xl mb-4">
              {t("section_about_tag")}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-[1.15] mb-6">
              {t("section_about_title_line1")}
              <br />
              {t("section_about_title_line2")}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8 max-w-lg">
              {t("section_about_desc")}
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-100/70 text-accent-800 text-sm font-semibold rounded-2xl hover:bg-accent-200/70 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("section_about_cta")}
              <i className="ri-arrow-right-up-line text-base"></i>
            </a>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-background-50">
        <div className="max-w-[900px] mx-auto text-center">
          <div className="mb-12 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Fashion%20product%20photography%20with%20sneakers%20designer%20bag%20and%20folded%20clothing%20arranged%20elegantly%20on%20warm%20textured%20background%20soft%20film%20grain%20effect%20desaturated%20warm%20tones%20editorial%20magazine%20quality%20artistic%20composition%20natural%20light%20gentle%20shadows%20high%20end%20aesthetic&width=900&height=450&seq=kakobuy-cta-v2-01&orientation=landscape"
              alt="Kakobuy Spreadsheet - najlepsze okazje"
              className="w-full object-cover"
            />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-8">
            {t("section_cta_title_line1")}
            <br />
            <span className="font-[var(--font-heading)] italic font-normal text-foreground-500">{t("section_cta_title_line2")}</span>
            <br />
            {t("section_cta_title_line3")}
          </h2>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex px-8 py-3.5 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("section_cta_button")}
          </a>
        </div>
      </section>

      {/* ===== TESTIMONIAL ===== */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-background-50">
        <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
          <div className="lg:w-[60%]">
            <div className="flex items-center gap-2 mb-8">
              <span className="w-2 h-2 rounded-full bg-foreground-900"></span>
              <span className="text-xs font-medium text-foreground-600">Opinie Użytkowników</span>
            </div>
            <blockquote className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-950 leading-[1.3] max-w-2xl">
              &ldquo;{t("testimonial_text")}&rdquo;
            </blockquote>
            <div className="mt-6 flex items-center gap-3">
              <span className="text-sm font-semibold text-foreground-900">{t("testimonial_name")}</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-verified-badge-fill text-accent-500 text-sm"></i>
              </div>
              <span className="text-xs text-foreground-500">{t("testimonial_badge")}</span>
            </div>
          </div>
          <div className="lg:w-[40%]">
            <div className="rounded-3xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Portrait%20of%20young%20woman%20smiling%20holding%20shopping%20package%20in%20bright%20modern%20interior%20with%20warm%20natural%20light%20clean%20white%20background%20casual%20stylish%20outfit%20authentic%20happy%20expression%20editorial%20portrait%20photography%20soft%20tones%20genuine%20moment%20lifestyle&width=500&height=600&seq=kakobuy-testimonial-v2-01&orientation=portrait"
                alt="Zadowolona użytkowniczka Kakobuy Spreadsheet"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-20 md:py-28 px-6 md:px-10 bg-background-100">
        <div className="max-w-[1100px] mx-auto">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 text-center mb-12">
            {t("section_faq_title")}
          </h2>
          <div className="space-y-3">
            {faqItems.map((item, idx) => (
              <div
                key={idx}
                className="rounded-2xl bg-background-50 overflow-hidden transition-colors"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer"
                >
                  <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                    {t(item.questionKey)}
                  </span>
                  <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${activeFaq === idx ? "rotate-45" : ""}`}>
                    <i className="ri-add-line text-foreground-500"></i>
                  </div>
                </button>
                {activeFaq === idx && (
                  <div className="px-6 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {t(item.answerKey)}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}